import { useState, useEffect, createContext, useContext, useCallback, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle2, AlertCircle, AlertTriangle, Info } from 'lucide-react';
import { cn } from '@/lib/utils';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  title: string;
  description?: string;
  duration?: number;
}

interface ToastContextType {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
  success: (title: string, description?: string) => void;
  error: (title: string, description?: string) => void;
  warning: (title: string, description?: string) => void;
  info: (title: string, description?: string) => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

// Type-driven visual config: which icon, which accent CSS var, which subtle
// surface tint. All values resolve from the design-system tokens so toasts
// theme correctly in light + dark and stay coherent with chips/buttons.
const TYPE_CONFIG: Record<
  ToastType,
  { icon: React.ElementType; accent: string; tint: string }
> = {
  success: {
    icon: CheckCircle2,
    accent: 'var(--color-accent)',
    tint: 'rgba(45, 212, 191, 0.10)',
  },
  error: {
    icon: AlertCircle,
    accent: 'var(--color-destructive)',
    tint: 'rgba(239, 68, 68, 0.10)',
  },
  warning: {
    icon: AlertTriangle,
    accent: 'var(--color-warning)',
    tint: 'rgba(245, 158, 11, 0.10)',
  },
  info: {
    icon: Info,
    accent: 'var(--color-primary)',
    tint: 'rgba(0, 102, 255, 0.10)',
  },
};

// Refined toast — matches the platform's glass-surface aesthetic. A subtle
// 3px accent bar on the left identifies the type without coloring the whole
// chip; surface uses var(--color-surface) so it sits cleanly on any theme.
function ToastItem({ toast, onRemove }: { toast: Toast; onRemove: () => void }) {
  const { t } = useTranslation();
  const cfg = TYPE_CONFIG[toast.type];
  const Icon = cfg.icon;

  useEffect(() => {
    const timer = setTimeout(() => {
      onRemove();
    }, toast.duration || 4000);
    return () => clearTimeout(timer);
  }, [toast.duration, onRemove]);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -16, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -8, scale: 0.96, transition: { duration: 0.15 } }}
      transition={{ type: 'spring', stiffness: 380, damping: 30 }}
      className={cn(
        'relative flex items-start gap-3 min-w-[280px] max-w-sm',
        'rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)]/95',
        'backdrop-blur-xl backdrop-saturate-150',
        'shadow-[0_12px_32px_-8px_rgba(0,0,0,0.35),0_2px_6px_-2px_rgba(0,0,0,0.18)]',
        'pl-4 pr-3 py-3 overflow-hidden',
      )}
      style={{
        // Tinted background wash matching the toast type — reads as identity
        // without overwhelming the readable text underneath.
        backgroundImage: `linear-gradient(to right, ${cfg.tint}, transparent 60%)`,
      }}
    >
      {/* Accent bar (left) — 3px solid type color */}
      <span
        className="absolute left-0 top-2 bottom-2 w-[3px] rounded-r-full"
        style={{ background: cfg.accent }}
        aria-hidden
      />

      {/* Icon disc — subtle tinted circle so it stands apart from the title */}
      <span
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
        style={{ background: cfg.tint }}
      >
        <Icon className="h-4 w-4" style={{ color: cfg.accent }} />
      </span>

      <div className="flex-1 min-w-0 pt-0.5">
        <p className="text-[13px] font-semibold leading-snug text-[var(--color-text)]">
          {toast.title}
        </p>
        {toast.description && (
          <p className="mt-0.5 text-[12px] leading-snug text-[var(--color-text-muted)]">
            {toast.description}
          </p>
        )}
      </div>

      <button
        type="button"
        onClick={onRemove}
        aria-label={t('layoutUi:toast.close')}
        className="shrink-0 -mr-1 -mt-1 h-7 w-7 inline-flex items-center justify-center rounded-md text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-[var(--color-surface-hover)] transition-colors"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </motion.div>
  );
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).substring(2, 9);
    setToasts((prev) => [...prev, { ...toast, id }]);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const success = useCallback((title: string, description?: string) => {
    addToast({ type: 'success', title, description });
  }, [addToast]);

  const error = useCallback((title: string, description?: string) => {
    addToast({ type: 'error', title, description });
  }, [addToast]);

  const warning = useCallback((title: string, description?: string) => {
    addToast({ type: 'warning', title, description });
  }, [addToast]);

  const info = useCallback((title: string, description?: string) => {
    addToast({ type: 'info', title, description });
  }, [addToast]);

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast, success, error, warning, info }}>
      {children}
      {/* Top-center stack with safe-area inset so it never hides under the
          mobile notch / dynamic island. Pointer-events handled per-item. */}
      <div
        className="fixed inset-x-0 top-0 z-[600] flex flex-col items-center gap-2 px-4 pointer-events-none"
        style={{ paddingTop: 'calc(var(--inset-top-safe) + 12px)' }}
      >
        <AnimatePresence initial={false}>
          {toasts.map((toast) => (
            <div key={toast.id} className="pointer-events-auto w-full max-w-sm">
              <ToastItem toast={toast} onRemove={() => removeToast(toast.id)} />
            </div>
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}

export default ToastProvider;
